import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import {
  CHECKOUT_MAX_AGE_MS,
  CHECKOUT_STARTED_KEY,
  POLL_INTERVAL_MS,
  POLL_MAX_MS,
  fetchActiveProTier,
  fireEntitlementFlipped,
  getEntitlementOverrideSnapshot,
  openProWelcome,
} from "@/lib/entitlementFlip";

/**
 * v1.3 spec §A.2-A.4: refetches entitlements on visibilitychange/focus so a
 * tab left open through a Stripe checkout picks up the free->pro flip
 * without a manual refresh. If checkout was recently started but the tier
 * hasn't flipped yet, polls briefly and shows a small non-blocking banner.
 *
 * ── Launch day, Aug 5 2026: why this also checks on mount ──────────────────
 *
 * Median opens Stripe in Safari (openExternal), so the purchase completes in
 * a different app entirely and the success_url never loads in the webview.
 * The only way back is the mom switching apps herself — and this component
 * had two holes on that path, both confirmed on device:
 *
 *   1. It bound visibilitychange/focus and never ran a check on mount. A cold
 *      start mounts already-visible and already-focused, so on a fresh launch
 *      NEITHER event fires and no check ever happens. She stayed free until
 *      something else happened to background the app.
 *   2. runCheck() returned early when she was already Pro, so the setup flow
 *      was only ever offered on a free->pro flip this component watched
 *      happen live. Cold-starting as a brand-new Pro — the single most common
 *      way to come back from Safari — skipped the flow silently.
 *
 * Opening the setup flow is now decoupled from having witnessed the flip: a
 * confirmed-Pro check offers it whenever she started a checkout from this
 * browser in the last CHECKOUT_MAX_AGE_MS, and localStorage
 * kin_pro_memory_setup_done (checked inside openProWelcome) is the one and
 * only duplicate guard on top of that.
 *
 * ⚠️ That checkout window is load-bearing, and the e2e suite is what proved
 * it. On a cold start "confirmed Pro" no longer means "just bought" — a mom
 * who has been Pro for weeks confirms Pro on every launch. Offering the
 * wizard on that alone put a fixed inset-0 z-[80] takeover over the app on
 * every Pro launch with empty storage, which failed 8 e2e specs by
 * intercepting every click. kin_checkout_started is the signal that she
 * bought from THIS browser: Pro.tsx writes it when she taps Upgrade, and
 * fireEntitlementFlipped clears it once the flip lands.
 *
 * Nothing here decides what anyone is ENTITLED to — that stays with
 * fetchActiveProTier and useAuth, and the unlock below is never conditional
 * on the checkout window. Only the modal is.
 */

/** True when a checkout was started from this browser recently enough to still
 *  be the one she just completed. Shared with maybeStartPolling below so the
 *  freshness window is defined once — the two must agree by construction, not
 *  by someone remembering to update both. */
const checkoutRecentlyStarted = (): boolean => {
  let startedAt: number | null = null;
  try {
    const raw = localStorage.getItem(CHECKOUT_STARTED_KEY);
    startedAt = raw ? Number(raw) : null;
  } catch {
    startedAt = null;
  }
  if (!startedAt || !Number.isFinite(startedAt)) return false;
  return Date.now() - startedAt < CHECKOUT_MAX_AGE_MS;
};

/** How often the in-flight-checkout sweep re-checks. Only ever does work
 *  while checkoutRecentlyStarted() is true, so it is bounded by the same
 *  30-minute window and costs one no-op tick otherwise. */
const RESUME_SWEEP_MS = 15_000;

// TODO(post-launch): remove this logging. It exists for the Aug 5 launch-day
// device tests, to tell "the check ran and found nothing" apart from "the
// check never ran at all" — the two failure modes look identical from the
// outside and we burned a device test round on not knowing which it was.
const debugResume = (message: string): void => {
  console.log(`[KIN_RESUME] ${message}`);
};

/** Age of the in-flight checkout flag, for the log line only. */
const checkoutFlagAgeLabel = (): string => {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(CHECKOUT_STARTED_KEY);
  } catch {
    return "unreadable";
  }
  const startedAt = raw ? Number(raw) : null;
  if (!startedAt || !Number.isFinite(startedAt)) return "absent";
  return `${((Date.now() - startedAt) / 60000).toFixed(1)}min`;
};

const EntitlementFlipListener = () => {
  const { user, isPro } = useAuth();
  const [bannerVisible, setBannerVisible] = useState(false);
  const isProRef = useRef(isPro);
  isProRef.current = isPro;

  useEffect(() => {
    const userId = user?.id;
    if (!userId) return;

    let cancelled = false;
    let pollTimer: number | undefined;
    let pollElapsed = 0;

    const stopPoll = () => {
      if (pollTimer !== undefined) {
        window.clearInterval(pollTimer);
        pollTimer = undefined;
      }
      pollElapsed = 0;
      setBannerVisible(false);
    };

    // Returns true if confirmed Pro (already, or just flipped), false if
    // still free, or null if the check was skipped/cancelled.
    const runCheck = async (): Promise<boolean | null> => {
      if (cancelled) return null;
      // Read before fireEntitlementFlipped below, which clears the flag.
      const justBought = checkoutRecentlyStarted();
      // TODO(post-launch): remove with the rest of the [KIN_RESUME] logging.
      // This is the one the Aug 5 device tests needed and did not have: the
      // existing lines prove the check RAN, but only this says whether the
      // wizard was offered or deliberately suppressed. Without it, "she came
      // back too late" and "the wizard is broken" look identical.
      debugResume(`justBought=${justBought} flagAge=${checkoutFlagAgeLabel()}`);
      if (isProRef.current || getEntitlementOverrideSnapshot() === true) {
        // Pro already, without this component having seen the flip happen. If
        // she bought from this browser just now, that's the cold start back
        // from Safari and nothing has offered her the setup flow yet — offer
        // it. Otherwise she's simply a Pro mom opening the app, and the only
        // correct thing to do is nothing. Grants nothing either way: she is
        // already Pro by the time we get here.
        if (justBought) openProWelcome();
        return true;
      }
      const activePro = await fetchActiveProTier(userId);
      if (cancelled) return null;
      if (activePro) {
        // The unlock runs unconditionally — a flip this component didn't
        // start (a comp grant, another device, a slow webhook) must still
        // re-render her as Pro without a refresh. Only the welcome modal is
        // gated, and only on whether this browser just bought.
        fireEntitlementFlipped(userId, { openWelcome: justBought });
        return true;
      }
      return false;
    };

    const maybeStartPolling = () => {
      if (pollTimer !== undefined) return;
      // Same predicate as before, now via the shared helper above.
      if (!checkoutRecentlyStarted()) return;

      setBannerVisible(true);
      pollElapsed = 0;
      pollTimer = window.setInterval(() => {
        pollElapsed += POLL_INTERVAL_MS;
        void runCheck().then((result) => {
          if (result === true || pollElapsed >= POLL_MAX_MS) {
            stopPoll();
          }
        });
      }, POLL_INTERVAL_MS);
    };

    const handleActivity = (trigger: string) => {
      if (document.visibilityState === "hidden") {
        debugResume(`${trigger} — skipped, document hidden`);
        return;
      }
      debugResume(`${trigger} — running check`);
      void runCheck().then((result) => {
        debugResume(`${trigger} — result=${String(result)}`);
        if (result === false) {
          maybeStartPolling();
        } else if (result === true) {
          stopPoll();
        }
      });
    };

    // Hole 1 above. Run the same check once, now, because on a cold start
    // neither listener below will ever fire on its own. Deliberately
    // handleActivity() and not runCheck() directly, so a cold start that
    // lands mid-webhook gets the existing brief poll (and its banner) exactly
    // like a resumed tab does. For a free user this costs one entitlements
    // read and does nothing else — maybeStartPolling still needs a fresh
    // kin_checkout_started to do anything at all.
    handleActivity("mount");

    const onVisibilityChange = () => handleActivity("visibilitychange");
    const onFocus = () => handleActivity("focus");
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", onFocus);

    // ── Median's resume hook ───────────────────────────────────────────────
    //
    // Aug 5 2026, launch day: the #623 fix unlocked the entitlement but the
    // setup wizard still did not fire on return from a Safari checkout in the
    // Median app. Cause: **Median does not dispatch a DOM event on resume.**
    // It invokes a GLOBAL FUNCTION named `median_app_resumed()` if the page
    // has defined one (docs.median.co/docs/app-resumed-callback). So on a
    // WKWebView resume both listeners above can stay completely silent, and
    // before this there was nothing else to notice she had come back.
    //
    // It is a single global slot, not a listener list. Median's own Custom
    // JavaScript config can define one too, so chain whatever was already
    // there and restore it on cleanup rather than deleting — clobbering
    // someone else's resume handler would be a nasty way to repay this.
    //
    // No app rebuild needed; the hook is pure JS and works on the shipped
    // shell. On Android it also fires after a native permission prompt, which
    // is harmless here (runCheck is cheap and gated).
    const globalWindow = window as unknown as Record<string, unknown>;
    const previousResumeHandler = globalWindow.median_app_resumed;
    globalWindow.median_app_resumed = (...args: unknown[]) => {
      handleActivity("median_app_resumed");
      if (typeof previousResumeHandler === "function") {
        try {
          (previousResumeHandler as (...a: unknown[]) => unknown)(...args);
        } catch {
          /* someone else's handler, not ours to fix */
        }
      }
    };

    // ── The no-event fallback ──────────────────────────────────────────────
    //
    // Belt and suspenders for the case this whole bug is made of: NO trigger
    // arrives at all. Even with the resume hook above, one silent shell or one
    // throttled timer and she is back to paying and getting nothing.
    //
    // This ticks every 15s but only does work while a checkout is genuinely in
    // flight, so it is bounded by the same 30-minute window and is a no-op
    // comparison otherwise — no entitlements read, no network, nothing. The
    // existing poll cannot cover this: it stops after POLL_MAX_MS (60s), which
    // has long expired by the time she finishes paying in Safari. Same shape
    // as TodayTab's stale-day interval, and for the same reason — the Median
    // shell resumes on the same page without a reload.
    const sweepTimer = window.setInterval(() => {
      if (!checkoutRecentlyStarted()) return;
      debugResume("interval sweep — checkout in flight, running check");
      void runCheck().then((result) => {
        debugResume(`interval sweep — result=${String(result)}`);
      });
    }, RESUME_SWEEP_MS);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", onFocus);
      globalWindow.median_app_resumed = previousResumeHandler;
      window.clearInterval(sweepTimer);
      stopPoll();
    };
  }, [user?.id]);

  if (!bannerVisible) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 60,
        display: "flex",
        justifyContent: "center",
        pointerEvents: "none",
      }}
    >
      <div
        className="font-body"
        style={{
          marginTop: 8,
          backgroundColor: "#5050C8",
          color: "#FFFFFF",
          fontSize: 13,
          fontWeight: 600,
          padding: "8px 16px",
          borderRadius: 999,
          boxShadow: "0 4px 12px rgba(28, 26, 62, 0.25)",
        }}
      >
        Finishing your upgrade... 💜
      </div>
    </div>
  );
};

export default EntitlementFlipListener;
